import type { SeatAddon, Seats, TripView } from "./api.ts";
import { lastDay } from "./format.ts";
import { RefundAction, refundLine } from "./Refund.tsx";
import { SettingsGroup } from "./SettingsGroup.tsx";
import { ButtonLink } from "./ui.tsx";

// A trip's spots on the web (features/paying.md, "Spots"): Trip settings' card People and spots,
// and the way to buy more that People offers when the trip is full. Buying is Stripe's Checkout, in this
// tab, coming back to where the person was.

const SPOTS_PER_BLOCK = 5;

// "$5", "$12.49".
export const dollars = (cents: number) => (cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`);

// How many blocks of 5 it takes for one more person to fit (more than one only on a trip left over its
// limit, its owner's plan ended), at least one.
export function blocksNeeded({ used, limit }: { used: number; limit: number | null }): number {
  if (limit === null) return 1;
  return Math.min(10, Math.max(1, Math.ceil((used + 1 - limit) / SPOTS_PER_BLOCK)));
}

// Where the owner buys more spots for a trip (`blocks` of 5, more to pick at checkout): with someone to
// let in once it's paid, and the page to come back to.
export function addSpotsPath(
  trip: string,
  { letIn, back, blocks = 1 }: { letIn?: string; back: string; blocks?: number },
): string {
  const params = new URLSearchParams({ trip, from: "web" });
  if (blocks > 1) params.set("blocks", String(blocks));
  if (letIn) params.set("let_in", letIn);
  params.set("return_to", back);
  return `/buy/seats?${params.toString()}`;
}

// "4 of 5 spots used, owner included", or why there's no limit.
export function spotsLine(trip: TripView, seats: Seats): string {
  if (seats.limit !== null) return `${seats.used} of ${seats.limit} spots used, owner included`;
  if (!seats.plan) return "Unlimited spots";
  if (trip.owner === trip.you) return `Unlimited spots: you're on ${seats.plan}`;
  const owner = trip.members.find((m) => m.id === trip.owner)?.name ?? "the owner";
  return `Unlimited spots: ${owner} is on ${seats.plan}`;
}

// Trip settings' card People and spots, for everyone: how many spots are used of how many; for the owner, Add 5
// more spots (none while the trip is read-only: get_trip's add_url is null then) and the add-ons bought;
// for the others, whose spots they are to add.
export function PeopleAndSpots({
  trip,
  seats,
  onChanged = () => {},
}: {
  trip: TripView;
  seats: Seats;
  onChanged?: () => void;
}) {
  const owner = trip.owner === trip.you;
  const ownerName = trip.members.find((m) => m.id === trip.owner)?.name ?? "The owner";
  const limited = seats.limit !== null;
  // Spots are for sale only while the trip can change (features/paying.md, "Spots").
  const forSale = limited && seats.add_url !== null;
  const { block, pass } = seats.prices;
  const blocks = blocksNeeded(seats);
  // The whole price of a bigger trip, while that's simply a pass and blocks on top.
  const totals =
    seats.coverage === "pass" && seats.limit === 5
      ? `10 people ${dollars(pass + block)} in total, 15 people ${dollars(pass + 2 * block)}.`
      : `${dollars(block)} for every 5 more people.`;
  return (
    <SettingsGroup id="spots-title" title="People and spots">
      <div className="tm-settings-part">
        <p className="callout">{spotsLine(trip, seats)}</p>
        {forSale && seats.limit !== null && seats.used > seats.limit && (
          <p className="caption tm-muted">Everyone in it stays. To let someone new in, add spots.</p>
        )}
        {limited && trip.locked && (
          <p className="caption tm-muted">No spots can be added while the trip is read-only.</p>
        )}
        {forSale && owner && (
          <>
            <ButtonLink
              size="sm"
              href={addSpotsPath(trip.id, { back: `/t/${trip.id}?settings=1`, blocks })}
              className="tm-edge-start"
            >
              Add {SPOTS_PER_BLOCK * blocks} more spots · {dollars(block * blocks)}
            </ButtonLink>
            <p className="caption tm-muted">Choose how many at checkout: {totals}</p>
          </>
        )}
        {forSale && !owner && <p className="caption tm-muted">Only {ownerName} can add spots.</p>}
      </div>
      {owner && seats.addons.length > 0 && (
        <ul className="tm-settings-part tm-spots-addons" aria-label="Spots bought">
          {seats.addons.map((a) => (
            <AddonRow key={a.id} addon={a} trip={trip} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </SettingsGroup>
  );
}

// "18 Oct", in the reader's own time zone.
const BOUGHT_ON = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });

function AddonRow({ addon, trip, onChanged }: { addon: SeatAddon; trip: TripView; onChanged: () => void }) {
  const spots = SPOTS_PER_BLOCK * addon.blocks;
  const offer = addon.refundable_until
    ? { until: addon.refundable_until, why: "none of its spots has been needed" }
    : null;
  const asked =
    addon.refund_request && addon.refund_request.status !== "refunded" ? refundLine(null, addon.refund_request) : null;
  const state =
    addon.status === "refunded"
      ? "Refunded"
      : addon.status === "disputed"
        ? "Payment disputed"
        : (asked ??
          (addon.refundable_until
            ? `Refundable until ${lastDay(addon.refundable_until)}, while none of its spots is needed`
            : addon.used
              ? "In use"
              : null));
  return (
    <li className="tm-spots-addon">
      <span className="tm-spots-addon-text">
        <span className="callout">
          +{spots} spots, {BOUGHT_ON.format(new Date(addon.bought_at))}
        </span>
        {state && <span className="caption tm-muted">{state}</span>}
      </span>
      <RefundAction
        target={{ type: "seat_addon", id: addon.id }}
        what={`+${spots} spots on ${trip.title}`}
        offer={offer}
        request={addon.refund_request}
        takesBack={`The ${spots} spots go from ${trip.title}.`}
        onChanged={onChanged}
      />
    </li>
  );
}

// Why a trip is read-only, and what that leaves (features/paying.md, "Refunds"): its Trip Pass refunded (its
// owner can get a new one for it), its payment disputed, or its refund on its way. Said where its notices are,
// and in the sheet a card's "Change with your AI" opens (AskAi.tsx).
export function lockedWords(trip: TripView): { text: string; more: string } | null {
  const locked = trip.locked;
  if (!locked) return null;
  const owner = trip.owner === trip.you;
  const ownerName = trip.members.find((m) => m.id === trip.owner)?.name ?? "its owner";
  const text =
    locked.why === "refund_pending"
      ? "This trip is read-only: its Trip Pass is being refunded."
      : locked.why === "disputed"
        ? "This trip is read-only while the payment for its Trip Pass is disputed."
        : "This trip is read-only: its Trip Pass was refunded.";
  const more =
    locked.why === "refunded"
      ? owner
        ? "Everything in it stays. A new pass makes it yours to plan again."
        : `Everything in it stays. ${ownerName} can get a Trip Pass for it to keep planning.`
      : locked.why === "disputed"
        ? "Everything in it stays, and you can still read it."
        : "You can still read it.";
  return { text, more };
}

// A read-only trip says so where its notices are. Reads still work.
export function LockedNotice({ trip }: { trip: TripView }) {
  const locked = trip.locked;
  const words = lockedWords(trip);
  if (!locked || !words) return null;
  const { text, more } = words;
  const owner = trip.owner === trip.you;
  const price = trip.seats ? dollars(trip.seats.prices.pass) : null;
  return (
    <div className="tm-locked" role="status">
      <span className="tm-locked-text">
        <span className="callout tm-billing-name">{text}</span>
        <span className="caption tm-muted">{more}</span>
      </span>
      {locked.why === "refunded" && owner && locked.buy_url && (
        <ButtonLink
          href={`/buy/trip-pass?trip=${encodeURIComponent(trip.id)}&from=web&return_to=${encodeURIComponent(`/t/${trip.id}`)}`}
          variant="primary"
          size="sm"
          className="tm-locked-buy"
        >
          Get a Trip Pass{price ? ` · ${price}` : ""}
        </ButtonLink>
      )}
    </div>
  );
}
